import { flow, makeObservable, observable, action, computed } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { Publication } from '@shared/types/publication';
import PublicationApi from '@shared/apis/PublicationApi';
import { BookmarkApi, ElasticSearchApi, PageNoteApi } from '@shared/apis';
import { Bookmark, Note, OpenBook } from '@shared/types/reader';
import { TYPE_SEARCH, TypeSearch } from '@shared/types/enums';

export default class ReaderStore extends BaseStore {
    sessionMode: 'preview' | 'reader' | null = null;
    openBooks: OpenBook[] = [];
    activeBookId: string | null = null;
    currentPageUrl: string | null = null;
    isLoadingPage: boolean = false;
    isSearchOpen: boolean = false;

    searchResults: any[] = [];
    searchTotal: number = 0;
    isSearching: boolean = false;
    searchPage: number = 1;
    activeSearchResultId: string | null = null;
    searchTerm: string = '';
    searchType: TypeSearch = TYPE_SEARCH.OR;

    isBookmarkNoteOpen: boolean = false;
    isPageBookmarked: boolean = false;
    currentBookmarkId: string | null = null;
    bookmarks: Bookmark[] = [];
    bookmarkPage: number = 1;
    bookmarkTotal: number = 0;
    isFetchingBookmarks: boolean = false;

    notes: Note[] = [];
    notePage: number = 1;
    noteTotal: number = 0;
    isFetchingNotes: boolean = false;
    currentPageNotes: Note[] = [];

    isNoteModalOpen: boolean = false;
    selectedText: string = '';
    selectedPosition: string = '';

    private getPageTextCallback: (() => Promise<string>) | null = null;

    constructor(rootStore: RootStore) {
        super(rootStore);

        makeObservable(this, {
            sessionMode: observable,
            openBooks: observable,
            activeBookId: observable,
            currentPageUrl: observable,
            isLoadingPage: observable,
            addBook: action.bound,
            removeBook: action.bound,
            setActiveBook: action.bound,
            setCurrentPage: action.bound,
            fetchPage: flow.bound,
            recordReading: flow.bound,
            recordRecentlyViewed: flow.bound,
            saveToSession: action.bound,
            loadFromSession: action.bound,
            setSessionMode: action.bound,

            search: flow.bound,
            setIsSearchOpen: action.bound,
            toggleSearch: action.bound,
            isSearchOpen: observable,
            searchResults: observable,
            searchTotal: observable,
            isSearching: observable,
            searchPage: observable,
            activeSearchResultId: observable,
            setActiveSearchResult: action.bound,
            searchTerm: observable,
            searchType: observable,
            setSearchType: action.bound,
            activeSearchHighlightWords: computed,

            isBookmarkNoteOpen: observable,
            bookmarks: observable,
            bookmarkPage: observable,
            bookmarkTotal: observable,
            isFetchingBookmarks: observable,
            setIsBookmarkNoteOpen: action.bound,
            toggleBookmarkNote: action.bound,
            isPageBookmarked: observable,
            currentBookmarkId: observable,
            fetchBookmarks: flow.bound,
            addBookmark: flow.bound,
            removeBookmark: flow.bound,
            checkIsPageBookmarked: flow.bound,
            handleBookmark: action.bound,
            setGetPageTextCallback: action.bound,

            notes: observable,
            notePage: observable,
            noteTotal: observable,
            isFetchingNotes: observable,
            currentPageNotes: observable,
            fetchNotes: flow.bound,
            fetchCurrentPageNotes: flow.bound,
            addNote: flow.bound,
            removeNote: flow.bound,
            isNoteModalOpen: observable,
            selectedText: observable,
            selectedPosition: observable,
            openNoteModal: action,
            closeNoteModal: action
        });
    }

    setSessionMode(mode: 'preview' | 'reader') {
        if (this.sessionMode !== mode) {
            this.sessionMode = mode;
            this.loadFromSession();
        }
    }

    private getSessionKey() {
        return this.sessionMode === 'preview' ? 'preview_open_books' : 'reader_open_books';
    }

    loadFromSession() {
        if (!this.sessionMode) return;
        try {
            const stored = sessionStorage.getItem(this.getSessionKey());
            if (stored) {
                const data = JSON.parse(stored);
                this.openBooks = data.openBooks || [];
                this.activeBookId = data.activeBookId || null;
            }
        } catch (error) {
            console.error('Failed to load reader session:', error);
        }
    }

    saveToSession() {
        if (!this.sessionMode) return;
        try {
            const data = {
                openBooks: this.openBooks,
                activeBookId: this.activeBookId
            };
            sessionStorage.setItem(this.getSessionKey(), JSON.stringify(data));
        } catch (error) {
            console.error('Failed to save reader session:', error);
        }
    }

    addBook(publication: Publication) {
        const index = this.openBooks.findIndex(b => b.publication.id === publication.id);
        if (index === -1) {
            this.openBooks.push({
                publication,
                currentPage: 1
            });
        } else {
            this.openBooks[index].publication = publication;
        }
        this.activeBookId = publication.id;
        this.saveToSession();
    }

    removeBook(publicationId: string) {
        const index = this.openBooks.findIndex(b => b.publication.id === publicationId);
        if (index > -1) {
            this.openBooks.splice(index, 1);

            if (this.activeBookId === publicationId) {
                this.activeBookId = this.openBooks.length > 0 ?
                    this.openBooks[Math.max(0, index - 1)].publication.id :
                    null;
            }
        }
        this.saveToSession();
    }

    setActiveBook(publicationId: string) {
        if (this.openBooks.some(b => b.publication.id === publicationId)) {
            this.activeBookId = publicationId;
            this.saveToSession();
        }
    }

    setCurrentPage(publicationId: string, pageNumber: number) {
        const book = this.openBooks.find(b => b.publication.id === publicationId);
        if (book) {
            const maxPages = book.publication.isPreview ?
                book.publication.previewPageCount :
                book.publication.totalNumberPages;

            book.currentPage = Math.max(1, Math.min(pageNumber, maxPages));
            this.saveToSession();
        }
    }

    getActiveBook(): OpenBook | undefined {
        return this.openBooks.find(b => b.publication.id === this.activeBookId);
    }

    *fetchPage(publicationId: string, pageNumber: number) {
        if (this.activeSearchResultId) {
            this.setActiveSearchResult(null);
        }

        const targetPage = !pageNumber || pageNumber < 1 ? 1 : pageNumber;

        const book = this.openBooks.find(b => b.publication.id === publicationId);

        if (book && book.publication.isPreview && targetPage > book.publication.previewPageCount) {
            return;
        }

        this.isLoadingPage = true;
        try {
            let res;
            if (book && !book.publication.isPreview) {
                res = yield PublicationApi.getPublicationPage(publicationId, targetPage);
            } else {
                res = yield PublicationApi.getPreviewPublicationPage(publicationId, targetPage);
            }

            if (res?.data?.data) {
                this.currentPageUrl = res.data.data;

                this.setCurrentPage(publicationId, targetPage);

                if (!book?.publication.isPreview) {
                    yield this.checkIsPageBookmarked();
                    yield this.fetchCurrentPageNotes(publicationId, targetPage);
                }
            }
        } catch (error) {
            this.currentPageUrl = null;
            console.error('Failed to fetch page:', error);
        } finally {
            this.isLoadingPage = false;
        }
    }

    *recordReading(id: string) {
        try {
            yield PublicationApi.recordReadingHistory(id);
        } catch (error) {
            console.error('Failed to record reading history:', error);
        }
    }

    *recordRecentlyViewed(id: string) {
        try {
            yield PublicationApi.recordRecentlyViewed(id);
        } catch (error) {
            console.error('Failed to record recently viewed:', error);
        }
    }

    setIsSearchOpen(isOpen: boolean) {
        this.isSearchOpen = isOpen;
    }

    toggleSearch() {
        if (this.isBookmarkNoteOpen) {
            this.closeBookmarkNote();
        }

        this.isSearchOpen = !this.isSearchOpen;

        if (!this.isSearchOpen) {
            this.closeSearch();
        }
    }

    closeSearch() {
        this.isSearchOpen = false;
        this.searchResults = [];
        this.searchTotal = 0;
        this.searchPage = 1;
        this.activeSearchResultId = null;
        this.searchTerm = '';
    }

    setActiveSearchResult(id: string | null) {
        this.activeSearchResultId = id;
    }

    get activeSearchHighlightWords(): string[] {
        if (!this.activeSearchResultId) return [];

        const activeResult = this.searchResults.find(r => r.id === this.activeSearchResultId);
        if (!activeResult || !activeResult.highlight) return [];

        const markRegex = /<mark>([^<]+)<\/mark>/gi;
        const words: string[] = [];
        let match;
        while ((match = markRegex.exec(activeResult.highlight)) !== null) {
            const word = match[1].trim();
            if (word && !words.includes(word)) {
                words.push(word);
            }
        }

        return words;
    }

    setSearchType(type: TypeSearch) {
        this.searchType = type;
    }

    *search(text: string, isLoadMore: boolean = false, typeSearch?: TypeSearch) {
        const activeBook = this.getActiveBook();
        if (!activeBook || !text) return;

        this.searchTerm = text;
        if (typeSearch !== undefined) {
            this.searchType = typeSearch;
        }
        this.isSearching = true;

        if (!isLoadMore) {
            this.searchPage = 1;
            this.searchTotal = 0;
            this.searchResults = [];
            this.activeSearchResultId = null;
        } else {
            this.searchPage += 1;
        }

        try {
            const res = (yield ElasticSearchApi.search({
                publicationId: activeBook.publication.id,
                text: this.searchTerm,
                size: 20,
                page: this.searchPage,
                typeSearch: this.searchType
            })).data;

            if (res) {
                const newResults = res?.data?.data || [];
                const total = res?.data?.total || 0;

                if (isLoadMore) {
                    this.searchResults.push(...newResults);
                } else {
                    this.searchResults = newResults;
                }
                this.searchTotal = total;
            }
        } catch (error) {
            console.error('Failed to search:', error);
        } finally {
            this.isSearching = false;
        }
    }

    setIsBookmarkNoteOpen(isOpen: boolean) {
        this.isBookmarkNoteOpen = isOpen;
    }

    toggleBookmarkNote() {
        if (this.isSearchOpen) {
            this.closeSearch();
        }

        this.isBookmarkNoteOpen = !this.isBookmarkNoteOpen;

        if (this.isBookmarkNoteOpen) {
            this.fetchBookmarks();
            this.fetchNotes();
        } else {
            this.closeBookmarkNote();
        }
    }

    closeBookmarkNote() {
        this.isBookmarkNoteOpen = false;

        this.bookmarks = [];
        this.bookmarkPage = 1;
        this.bookmarkTotal = 0;
        this.isFetchingBookmarks = false;

        this.notes = [];
        this.notePage = 1;
        this.noteTotal = 0;
        this.isFetchingNotes = false;
    }

    *checkIsPageBookmarked() {
        if (!this.activeBookId) {
            this.isPageBookmarked = false;
            this.currentBookmarkId = null;
            return;
        }

        const activeBook = this.getActiveBook();
        if (!activeBook) return;

        try {
            const res = (yield BookmarkApi.checkBookmark(
                this.activeBookId,
                activeBook.currentPage
            )).data;

            if (res?.data) {
                this.isPageBookmarked = res.data.isBookmark;
                this.currentBookmarkId = res.data.id || null;
            } else {
                this.isPageBookmarked = false;
                this.currentBookmarkId = null;
            }
        } catch (error) {
            console.error('Failed to check bookmark status:', error);
            this.isPageBookmarked = false;
        }
    }

    *fetchBookmarks(isLoadMore: boolean = false) {
        if (!this.activeBookId) return;

        this.isFetchingBookmarks = true;
        if (!isLoadMore) {
            this.bookmarkPage = 1;
            this.bookmarks = [];
        } else {
            this.bookmarkPage += 1;
        }

        try {
            const res = (yield BookmarkApi.getBookmarks({
                publicationId: Number(this.activeBookId),
                page: this.bookmarkPage,
                size: 20
            })).data;

            if (res?.data) {
                const newItems = (res.data.elements || []) as Bookmark[];
                if (isLoadMore) {
                    this.bookmarks.push(...newItems);
                } else {
                    this.bookmarks = newItems;
                }
                this.bookmarkTotal = res.data.pagination.totalRecord || 0;
            }
        } catch (error) {
            console.error('Failed to fetch bookmarks:', error);
        } finally {
            this.isFetchingBookmarks = false;
        }
    }

    setGetPageTextCallback(callback: (() => Promise<string>) | null) {
        this.getPageTextCallback = callback;
    }

    *addBookmark() {
        const activeBook = this.getActiveBook();
        if (!activeBook || !this.activeBookId) return;

        try {
            let content = '';
            if (this.getPageTextCallback) {
                content = yield this.getPageTextCallback();
            }

            const res = (yield BookmarkApi.addBookmark({
                publicationId: Number(this.activeBookId),
                pageNumber: activeBook.currentPage,
                content
            })).data;

            if (res?.ok) {
                yield this.fetchBookmarks();
                yield this.checkIsPageBookmarked();
            }
        } catch (error) {
            console.error('Failed to add bookmark:', error);
        }
    }

    *removeBookmark(id?: string | number) {
        const bookmarkId = id || this.currentBookmarkId;
        if (!bookmarkId) return;

        try {
            const res = (yield BookmarkApi.deleteBookmark(bookmarkId)).data;

            if (res) {
                yield this.fetchBookmarks();
                yield this.checkIsPageBookmarked();
            }
        } catch (error) {
            console.error('Failed to remove bookmark:', error);
        }
    }

    handleBookmark() {
        if (this.isPageBookmarked) {
            this.removeBookmark();
        } else {
            this.addBookmark();
        }
    }

    *fetchNotes(isLoadMore: boolean = false) {
        this.isFetchingNotes = true;
        if (!isLoadMore) {
            this.notePage = 1;
            this.notes = [];
        } else {
            this.notePage += 1;
        }

        try {
            const res = (yield PageNoteApi.getPageNotes({
                publicationId: this.activeBookId,
                page: this.notePage,
                size: 20
            })).data;
            if (res?.data) {
                const newItems = (res.data.elements || []) as Note[];
                if (isLoadMore) {
                    this.notes.push(...newItems);
                } else {
                    this.notes = newItems;
                }
                this.noteTotal = res.data.pagination.totalRecord || 0;
            }
        } catch (error) {
            console.error('Failed to fetch notes:', error);
        } finally {
            this.isFetchingNotes = false;
        }
    }

    *addNote(highlight: string, content: string, position: string) {
        const activeBook = this.getActiveBook();
        if (!activeBook || !this.activeBookId) return;

        try {
            yield PageNoteApi.createPageNote({
                publicationId: Number(this.activeBookId),
                pageNumber: activeBook.currentPage,
                highlight,
                content,
                position
            });
            yield this.fetchNotes();
            yield this.fetchCurrentPageNotes(this.activeBookId, activeBook.currentPage);
        } catch (error) {
            console.error('Failed to add note:', error);
        }
    }

    *fetchCurrentPageNotes(publicationId: string, pageNumber: number) {
        try {
            const res = (yield PageNoteApi.getPageNotes({
                publicationId,
                pageNumber,
                size: 100
            })).data;

            if (res?.data) {
                this.currentPageNotes = (res.data.elements || res.data || []) as Note[];
            } else {
                this.currentPageNotes = [];
            }
        } catch (error) {
            console.error('Failed to fetch current page notes:', error);
            this.currentPageNotes = [];
        }
    }

    *removeNote(id: string | number) {
        try {
            yield PageNoteApi.deletePageNote(id);
            yield this.fetchNotes();
            const activeBook = this.getActiveBook();
            if (activeBook && this.activeBookId) {
                yield this.fetchCurrentPageNotes(this.activeBookId, activeBook.currentPage);
            }
        } catch (error) {
            console.error('Failed to remove note:', error);
        }
    }

    openNoteModal = (text: string, position: string = '') => {
        this.selectedText = text;
        this.selectedPosition = position;
        this.isNoteModalOpen = true;
    };

    closeNoteModal = () => {
        this.isNoteModalOpen = false;
        this.selectedText = '';
        this.selectedPosition = '';
    };

    clean() {
        super.clean();
        this.openBooks = [];
        this.activeBookId = null;
        this.currentPageUrl = null;
        this.isLoadingPage = false;
    }
}
